import { Check, CheckCheck } from 'lucide-react';
import { Message } from '@/stores/messageStore';
import { formatTime } from '@kulum/shared';

interface MessageBubbleProps {
  message: Message;
  isOwn: boolean;
  showTimestamp?: boolean;
}

export function MessageBubble({ message, isOwn, showTimestamp = true }: MessageBubbleProps) {
  const { content, type, imageUrl, sentAt, readAt } = message;

  return (
    <div className={`mb-3 flex items-end gap-2 ${isOwn ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`
          max-w-[72%]
          ${isOwn
            ? 'rounded-2xl rounded-br-sm bg-[#B87333] text-white'
            : 'rounded-2xl rounded-bl-sm bg-white text-[#1A1A1A]'
          }
          ${type === 'image' ? 'p-1' : 'px-4 py-2'}
        `}
        style={{
          boxShadow: isOwn
            ? '0 2px 10px rgba(184,115,51,0.22)'
            : '0 1px 3px rgba(0,0,0,0.07)',
        }}
      >
        {/* Text message */}
        {type === 'text' && (
          <p className="text-[14px] whitespace-pre-wrap break-words leading-relaxed">{content}</p>
        )}

        {/* Image message */}
        {type === 'image' && imageUrl && (
          <div className="relative">
            <img
              src={imageUrl}
              alt="送信画像"
              className="max-w-full rounded-xl cursor-pointer hover:opacity-90 transition"
              onClick={() => window.open(imageUrl, '_blank')}
            />
          </div>
        )}

        {/* Timestamp and read status */}
        {showTimestamp && (
          <div className={`flex items-center gap-1 mt-1 ${isOwn ? 'justify-end' : 'justify-start'}`}>
            <span className={`text-[10px] ${isOwn ? 'text-white/75' : 'text-[#AAAAAA]'}`}>
              {formatTime(sentAt)}
            </span>
            {isOwn && (
              <span className={`${readAt ? 'text-[#F5EDE2]' : 'text-white/55'}`}>
                {readAt ? (
                  <CheckCheck className="h-3.5 w-3.5" />
                ) : (
                  <Check className="h-3.5 w-3.5" />
                )}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// Date separator component
interface DateSeparatorProps {
  date: Date;
}

export function DateSeparator({ date }: DateSeparatorProps) {
  const formatDateLabel = (date: Date): string => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const targetDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const diffDays = Math.floor((today.getTime() - targetDate.getTime()) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return '今日';
    if (diffDays === 1) return '昨日';
    if (diffDays < 7) return `${diffDays}日前`;

    return date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div className="flex items-center justify-center my-4">
      <div className="rounded-full bg-[#ECEAE6] px-4 py-1 text-[11px] text-[#888]">
        {formatDateLabel(date)}
      </div>
    </div>
  );
}
